import type {
  NavigationPageDisplayClasses,
  NavigationPageDisplaySettings,
} from "../../../types/blocks/navigation/NavigationPageOption";

export type FieldKind =
  | "text"
  | "textarea"
  | "mono"
  | "path"
  | "image"
  | "colour"
  | "external"
  | "interactive";

export type NavigationClassFieldKey =
  | "card"
  | "description"
  | "icon"
  | "interactive";

export type NavigationFieldKey =
  | "title"
  | "description"
  | "icon"
  | "iconColor"
  | "image"
  | "url"
  | "external"
  | "interactive"
  | `classes.${NavigationClassFieldKey}`;

export interface FieldDef {
  key: NavigationFieldKey;
  label: string;
  kind: FieldKind;
  classKey?: NavigationClassFieldKey;
}

export type NavigationFieldValue = string | boolean | undefined;

export type NavigationFieldTarget =
  & Partial<NavigationPageDisplaySettings>
  & Record<string, unknown>;

export const NAV_FIELDS: readonly FieldDef[] = [
  { key: "title", label: "Title", kind: "text" },
  { key: "description", label: "Description", kind: "textarea" },
  { key: "icon", label: "Icon", kind: "mono" },
  { key: "iconColor", label: "Icon color", kind: "colour" },
  { key: "image", label: "Image", kind: "image" },
  { key: "url", label: "Destination", kind: "path" },
  { key: "external", label: "Open behavior", kind: "external" },
  { key: "interactive", label: "Clickable", kind: "interactive" },
  { key: "classes.card", label: "Card classes", kind: "mono", classKey: "card" },
  {
    key: "classes.description",
    label: "Desc classes",
    kind: "mono",
    classKey: "description",
  },
  { key: "classes.icon", label: "Icon classes", kind: "mono", classKey: "icon" },
  {
    key: "classes.interactive",
    label: "Hover classes",
    kind: "mono",
    classKey: "interactive",
  },
] as const;

export const FIELD_BY_KEY = Object.fromEntries(
  NAV_FIELDS.map((field) => [field.key, field]),
) as Record<NavigationFieldKey, FieldDef>;

const CLASS_FIELD_SEEDS: Record<NavigationClassFieldKey, string> = {
  card: "bg-muted",
  description: "text-muted-foreground",
  icon: "text-primary",
  interactive: "group-hover:bg-accent/20",
};

function cloneClasses(
  classes: NavigationPageDisplayClasses | undefined,
): NavigationPageDisplayClasses {
  return { ...(classes ?? {}) };
}

function cleanClasses(
  classes: NavigationPageDisplayClasses | undefined,
): NavigationPageDisplayClasses | undefined {
  const next = Object.fromEntries(
    Object.entries(classes ?? {}).filter(([, value]) => isSet(value)),
  ) as NavigationPageDisplayClasses;

  return Object.keys(next).length ? next : undefined;
}

function textValue(value: unknown): string {
  return String(value ?? "");
}

export function isSet(value: unknown): boolean {
  if (typeof value === "boolean") return true;
  if (typeof value === "string") return value.trim().length > 0;
  return value !== undefined && value !== null;
}

export function getField(
  source: NavigationFieldTarget | null | undefined,
  def: FieldDef,
): NavigationFieldValue {
  if (!source) return undefined;
  if (def.classKey) return source.classes?.[def.classKey];
  if (def.key === "url") {
    return textValue(isSet(source.url) ? source.url : source.to);
  }
  const value = source[def.key];
  return typeof value === "boolean" || typeof value === "string"
    ? value
    : undefined;
}

export function setField<T extends NavigationFieldTarget>(
  source: T,
  def: FieldDef,
  value: unknown,
): T {
  const next = {
    ...source,
    classes: cloneClasses(source.classes),
  } as T;

  if (def.classKey) {
    next.classes = cleanClasses({
      ...cloneClasses(source.classes),
      [def.classKey]: textValue(value),
    });
    return next;
  }

  if (def.key === "url") {
    const destination = textValue(value);
    next.url = destination;
    next.to = destination;
    return next;
  }

  if (def.kind === "external" || def.kind === "interactive") {
    next[def.key] = value === undefined ? undefined : Boolean(value);
    return next;
  }

  next[def.key] = textValue(value);
  return next;
}

export function unsetField<T extends NavigationFieldTarget>(
  source: T,
  def: FieldDef,
): T {
  const next = {
    ...source,
    classes: cloneClasses(source.classes),
  } as T;

  if (def.classKey) {
    const classes = cloneClasses(source.classes);
    delete classes[def.classKey];
    next.classes = cleanClasses(classes);
    return next;
  }

  if (def.key === "url") {
    next.url = "";
    next.to = "";
    return next;
  }

  if (def.kind === "external" || def.kind === "interactive") {
    next[def.key] = undefined;
    return next;
  }

  next[def.key] = "";
  return next;
}

export function formatValue(def: FieldDef, value: unknown): string {
  if (!isSet(value)) {
    if (def.kind === "external" || def.kind === "interactive") return "Inherit";
    return "—";
  }

  if (def.kind === "external") return value ? "New tab" : "Same tab";
  if (def.kind === "interactive") return value ? "Clickable" : "Not clickable";

  return String(value);
}

export function summarizeSetFields(
  source: NavigationFieldTarget | null | undefined,
  limit = 3,
): string {
  const labels = NAV_FIELDS
    .filter((field) => isSet(getField(source, field)))
    .map((field) => field.label);

  if (!labels.length) return "Nothing set yet";
  if (labels.length <= limit) return labels.join(" · ");
  return `${labels.slice(0, limit).join(" · ")} +${labels.length - limit}`;
}

export function seedFieldValue(
  def: FieldDef,
  resolvedValue: unknown,
): NavigationFieldValue {
  if (def.kind === "external" || def.kind === "interactive") return false;
  if (def.classKey) return CLASS_FIELD_SEEDS[def.classKey];
  if (def.kind === "colour" && !isSet(resolvedValue)) return "var(--primary)";
  if (
    (def.kind === "text"
      || def.kind === "textarea"
      || def.kind === "mono"
      || def.kind === "colour"
      || def.kind === "path"
      || def.kind === "image")
    && isSet(resolvedValue)
  ) {
    return String(resolvedValue);
  }
  return "";
}
